import useLocalStorage from "@/hooks/useLocalStorage";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

type SidebarContextType = {
  isSidebarOpen: boolean;
  handleToggleSidebar: () => void;
  showSidebarOverlay: boolean;
  isShowingIconsOnly: boolean;
  setIsShowingIconsOnly: (value: boolean) => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

type SidebarProviderProps = {
  children: ReactNode;
};

const ScreenWidth = 768;

function SidebarProvider({ children }: SidebarProviderProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(
    window.innerWidth >= ScreenWidth ? true : false,
  );

  const [isShowingIconsOnly, setIsShowingIconsOnly] = useLocalStorage(false, "isShowingIconsOnly");

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= ScreenWidth) {
        setIsSidebarOpen(true);
        
      } else {
        setIsSidebarOpen(false);
        setIsShowingIconsOnly(false);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [setIsShowingIconsOnly]);

  const handleToggleSidebar = () => {
    if (window.innerWidth >= ScreenWidth) return;
    setIsSidebarOpen((prev) => !prev);
  };

  const showSidebarOverlay = isSidebarOpen && window.innerWidth < ScreenWidth;

  return (
    <SidebarContext.Provider
      value={{
        isSidebarOpen,
        handleToggleSidebar,
        showSidebarOverlay,
        isShowingIconsOnly,
        setIsShowingIconsOnly
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { SidebarProvider, useSidebar };
